import * as Sentry from '@sentry/vue';
import { store } from '@/app/store';
import { safe } from './safe';
import { state } from './state';

// Matches the shape `@sentry/vue` expects for the `tags` field. Declared once
// here so captureException/captureMessage share one cast target.
type SentryTags = Record<string, string | number | boolean | null | undefined>;

interface CaptureOptions {
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
  // Force a stable grouping fingerprint. Use for issues that would
  // otherwise split across many groups (one per endpoint URL etc.)
  // when they all share one root cause.
  fingerprint?: string[];
  // Override the exception mechanism. app.js recaptures the real thrown object
  // from the window 'error' / 'unhandledrejection' events (Sentry's built-in
  // handlers lose it on WebKit) and must restore the true crash mechanism
  // (type=onerror/onunhandledrejection, handled=false) instead of the
  // captureException default of generic/handled=true, so these keep counting
  // as unhandled crashes.
  mechanism?: { type: string; handled: boolean };
}

// Dynamic session context from store for error captures. Provides current-step
// from URL hash, which setCurrentStep's cached value may not reflect during
// error handlers running before router completes navigation.
export function getSessionContext(): Record<string, unknown> {
  try {
    // store.session is a pre-existing untyped field on the legacy reactive
    // store (not declared in types/global.d.ts). Access it via a targeted
    // unknown cast rather than fighting the inferred type.
    const storeAny = store as unknown as {
      sessionId?: string | null;
      kiosk?: { slug?: string };
      kioskId?: string;
      session?: { createdAt?: Date | string };
    };
    const sessionId = storeAny.sessionId;
    const session = storeAny.session;
    return {
      // Full session IDs are intentional Sentry metadata. Do not shorten them:
      // they are the join key to Postgres session rows and backend logs.
      session_id: sessionId,
      kiosk_slug: storeAny.kiosk?.slug,
      kiosk_id: storeAny.kioskId,
      current_step: window.location.hash?.replace('#/', '') || 'unknown',
      session_created_at:
        (session?.createdAt as Date | undefined)?.toISOString?.() ??
        session?.createdAt,
    };
  } catch (e) {
    return { context_error: (e as Error).message };
  }
}

interface NonErrorCaptureMeta {
  original_type: string;
  original_value: string;
}

// Sentry synthesizes a stackless, hard-to-place event when captureException is
// handed a non-Error primitive (string, number, boolean, …) or null/undefined —
// a caught value that was never an Error. Wrapping it in a real Error *here*
// gives the event a stack that walks back to the caller and a readable title,
// and tags it NonErrorCapture so it groups apart from genuine `throw new Error`.
//
// Objects are deliberately left as-is: Sentry already renders them as "Object
// captured as exception with keys […]", and beforeSend (init.ts) inspects the
// untouched object through hint.originalException — the ui.showError overlay
// drop keys on `uuid`/`hide`, the abort drop on `.context`. Wrapping an object
// would swap out originalException and blind those filters.
export function normalizeCaptureInput(error: unknown): {
  value: unknown;
  meta?: NonErrorCaptureMeta;
} {
  if (error instanceof Error) return { value: error };
  if (error !== null && typeof error === 'object') return { value: error };

  const originalType = error === null ? 'null' : typeof error;
  // String() never throws on a primitive (unlike `${symbol}` interpolation).
  const asString = error === null ? 'null' : String(error);
  const description =
    error === undefined || error === null
      ? `captureException received ${asString}`
      : asString;

  const wrapped = new Error(description);
  wrapped.name = 'NonErrorCapture';
  return {
    value: wrapped,
    meta: { original_type: originalType, original_value: asString },
  };
}

export function captureException(
  error: unknown,
  context: CaptureOptions = {}
): void {
  const { tags = {}, extra = {}, level = 'error', mechanism } = context;
  const { value, meta } = normalizeCaptureInput(error);
  const captureContext = {
    tags: tags as SentryTags,
    extra: {
      ...extra,
      ...(meta ? { non_error_capture: meta } : {}),
      sessionContext: state.sessionContext,
      kioskContext: state.kioskContext,
      dynamicContext: getSessionContext(),
    },
    level,
  };
  safe('captureException', () =>
    // A caller-supplied mechanism must ride in an EventHint: the plain-object
    // second arg is parsed as a CaptureContext, which has no mechanism field.
    // Without one, keep the CaptureContext form so existing call sites (and
    // their event shape) are unchanged.
    mechanism
      ? Sentry.captureException(value, { captureContext, mechanism })
      : Sentry.captureException(value, captureContext)
  );
}

export function captureMessage(
  message: string,
  context: CaptureOptions = {}
): void {
  const { tags = {}, extra = {}, level = 'info', fingerprint } = context;
  safe('captureMessage', () =>
    Sentry.captureMessage(message, {
      tags: tags as SentryTags,
      extra,
      level,
      ...(fingerprint && { fingerprint }),
    })
  );
}

export function addBreadcrumb(
  category: string,
  message: string,
  data: Record<string, unknown> = {},
  level: Sentry.SeverityLevel = 'info',
  type?: string
): void {
  safe('addBreadcrumb', () =>
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level,
      ...(type && { type }),
      timestamp: Date.now() / 1000,
    })
  );
}

export function setTag(key: string, value: unknown): void {
  safe('setTag', () => Sentry.setTag(key, value as string));
}

export function setContext(
  name: string,
  context: Record<string, unknown> | null
): void {
  safe('setContext', () => Sentry.setContext(name, context));
}

export function addAttachment(
  filename: string,
  data: string | Uint8Array | ArrayBuffer | Record<string, unknown>,
  contentType = 'application/json'
): void {
  safe('addAttachment', () => {
    let attachmentData: string | Uint8Array;
    if (typeof data === 'string') {
      attachmentData = data;
    } else if (data instanceof Uint8Array) {
      attachmentData = data;
    } else if (data instanceof ArrayBuffer) {
      attachmentData = new Uint8Array(data);
    } else {
      attachmentData = JSON.stringify(data);
    }
    Sentry.getCurrentScope().addAttachment({
      filename,
      data: attachmentData,
      contentType,
    });
  });
}

function sanitizeState(
  state_: unknown,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (state_ === null || typeof state_ !== 'object') return state_;
  if (seen.has(state_)) return '[Circular]';
  seen.add(state_);

  const source = state_ as Record<string, unknown>;
  const sanitized: Record<string, unknown> | unknown[] = Array.isArray(state_)
    ? []
    : {};

  // Object.keys() avoids iterating inherited prototype properties (vs for..in).
  for (const key of Object.keys(source)) {
    const value = source[key];
    // Operational join keys stay verbatim for debugging. Do not add masking for
    // session IDs, ticket/download/GoPhoto codes, API keys, payment intents,
    // Stripe accounts, reader IDs, visitor IDs, or device IDs in this state dump.
    // The owner has dashboard/database access and needs exact values in Sentry.
    if (key === 'paymentIntent' || key === 'stripeAccountId') {
      (sanitized as Record<string, unknown>)[key] = value;
    } else if (typeof value === 'string' && value.startsWith('blob:')) {
      (sanitized as Record<string, unknown>)[key] = '[blob URL]';
    } else if (typeof value === 'object' && value !== null) {
      (sanitized as Record<string, unknown>)[key] = sanitizeState(value, seen);
    } else {
      (sanitized as Record<string, unknown>)[key] = value;
    }
  }

  return sanitized;
}

export function captureSessionState(stateToCapture: unknown): void {
  if (!state.initialized) return;
  const sanitized = sanitizeState(stateToCapture);
  addAttachment(
    'session-state.json',
    JSON.stringify(sanitized, null, 2),
    'application/json'
  );
}
